import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import productAnalyticsService from '../../services/api/product-analytics.service';
import { UserProfile } from '../../services/api/product-analytics.service';

interface TimelineItem {
  type: string;
  timestamp: string;
  name: string;
  details?: any;
}

const eventIcon = (type: string, eventType?: string) => {
  if (type === 'page_view') return { icon: '\uD83D\uDCC4', label: 'Page View', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  if (eventType === 'click') return { icon: '\uD83D\uDDB1\uFE0F', label: 'Click', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
  if (eventType === 'form_submit') return { icon: '\uD83D\uDCDD', label: 'Form', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
  return { icon: '\u26A1', label: 'Event', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
};

export default function UserProfilePage() {
  const { t } = useTranslation('analytics');
  const { userId } = useParams<{ userId: string }>();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const appId = selectedAppId || 'all';

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const [prof, tl] = await Promise.allSettled([
        productAnalyticsService.getUserProfile(userId, appId),
        productAnalyticsService.getUserTimeline(userId, appId),
      ]);
      if (prof.status === 'fulfilled') setProfile(prof.value);
      if (tl.status === 'fulfilled') setTimeline(tl.value || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [userId, appId]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Back button */}
        <Link to="/analytics/overview" className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-cyan-600 transition-colors">
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          {t('userProfile.backToAnalytics')}
        </Link>

        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('userProfile.title')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('userProfile.subtitle')}</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* User Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.userId')}</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white break-all">{userId}</p>
            </div>
            {profile?.email && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.email')}</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{profile.email}</p>
              </div>
            )}
            {profile?.name && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.name')}</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{profile.name}</p>
              </div>
            )}
            {profile && (
              <>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.firstSeen')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{fmtDate(profile.first_seen_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.lastSeen')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{fmtDate(profile.last_seen_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.totalSessions')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{profile.session_count}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.totalEvents')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{profile.total_events}</p>
                </div>
              </>
            )}
            {!profile && (
              <div className="col-span-full">
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('userProfile.userNotFound')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{t('userProfile.timeline')}</h2>
          {timeline.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('userProfile.noActivity')}</div>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

              <div className="space-y-4">
                {timeline.map((item, i) => {
                  const { icon, label, color } = eventIcon(item.type, item.details?.eventType);
                  return (
                    <div key={i} className="relative flex items-start pl-12">
                      {/* Icon circle */}
                      <div className={`absolute left-2.5 w-5 h-5 rounded-full flex items-center justify-center text-xs ${color} ring-4 ring-white dark:ring-gray-800`}>
                        <span style={{ fontSize: '10px' }}>{icon}</span>
                      </div>

                      <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>{label}</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">{fmtDate(item.timestamp)}</span>
                        </div>
                        {item.details?.pageUrl && (
                          <p className="text-xs text-gray-500 truncate">{item.details.pageUrl}</p>
                        )}
                        {item.details?.properties && Object.keys(item.details.properties).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {Object.entries(item.details.properties).slice(0, 5).map(([k, v]) => (
                              <span key={k} className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                                {k}: {String(v)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
